/**
 * FIX-NOTIF N-5 - ONE policy for "which notification pops up as a toast", for the employee and the
 * customer alike (Q5): only what asks for a reaction - a request that reached me, a reply, an
 * approval waiting for / decided on my request, being added to a conversation or a task. Everything
 * else stays in the bell only. Pure; unit-tested in utils/__tests__/fixNotifN5.test.ts.
 */
import type { Notification } from '../types';
import { toTicketArrival } from '../components/tickets/ticketArrivalModel';
import type { NotificationKind } from '../components/notificationView';

export type ToastAudience = 'staff' | 'portal';

export interface NotificationToast {
  id: string;
  /** Icon of the toast (Q1/Q2: by kind; a reply shows the sender's avatar instead). */
  kind: NotificationKind;
  /** i18n key + its parameters (the hook translates, so the model stays pure). */
  messageKey: string;
  params: Record<string, string>;
  /** A second line under the message (a forwarded note, a reply preview). */
  note: string;
  /** A reply carries the sender's name for the avatar circle. */
  avatar: string | null;
  /** Where "פתיחה" leads: the server's link (one resolver for the bell, push and this). */
  link: string | null;
  /** An approval notice also refreshes the portal's lists and counters at once (FIX-APPRLIVE, D-257). */
  refreshesApprovals: boolean;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function toNotificationToast(n: Notification, audience: ToastAudience): NotificationToast | null {
  const m = (n.metadata ?? {}) as Record<string, unknown>;
  const title = str(m.ticket_title) || str(m.title) || n.ticket_title || '';
  const sender = str(m.sender_name);
  const base = { id: n.id, link: n.link ?? null, note: '', avatar: null as string | null, refreshesApprovals: false };

  switch (n.event_type) {
    case 'ticket_approval_requested':
      return { ...base, kind: 'approval', messageKey: 'portal.approvalLive.requested', params: { title: str(m.title), name: str(m.requester_name) || '—' }, refreshesApprovals: true };
    case 'ticket_approval_decided':
      return { ...base, kind: 'approval', messageKey: str(m.decision) === 'rejected' ? 'portal.approvalLive.rejected' : 'portal.approvalLive.approved', params: { title: str(m.title) }, refreshesApprovals: true };
    case 'conversation_started':
      return { ...base, kind: 'chat', messageKey: sender ? 'portalP19.toast.conversationStarted' : 'portalP19.toast.conversationStartedNoName', params: { name: sender, title } };
    case 'comment_added': {
      if (!sender && audience === 'staff') return null; // the chat bubble always named its sender
      const preview = str(m.message_preview);
      if (audience === 'portal') {
        return { ...base, kind: 'chat', messageKey: sender ? 'portalP19.toast.reply' : 'portalP19.toast.replyNoName', params: { name: sender, title }, note: '', avatar: sender || null };
      }
      return { ...base, kind: 'chat', messageKey: 'notifToast.reply', params: { name: sender, title }, note: preview, avatar: sender };
    }
    case 'ticket_completed':
      return audience === 'portal' ? { ...base, kind: 'ticket', messageKey: 'portalP19.toast.completed', params: { title } } : null;
    case 'task_assigned':
      return audience === 'staff' ? { ...base, kind: 'task', messageKey: 'notifToast.taskAssigned', params: { title } } : null;
    case 'ticket_created':
    case 'ticket_assigned':
    case 'ticket_forwarded': {
      if (audience !== 'staff') return null; // a customer is never told "a new request reached you"
      const a = toTicketArrival(n);
      if (!a) return null;
      const key = a.kind === 'forwarded' ? 'ticketArrival.forwarded' : a.kind === 'assigned' ? 'ticketArrival.assigned' : 'ticketArrival.new';
      return { ...base, kind: 'ticket', messageKey: key, params: { from: a.from, title: a.title }, note: a.note, link: n.link ?? `/tickets/${a.ticketId}` };
    }
    default:
      return null;
  }
}

/**
 * The toasts for notifications not seen before. The first poll only learns what is already there
 * (no storm on login); a notice already read never pops up; `known` is updated in place.
 */
export function newNotificationToasts(rows: Notification[], known: Set<string>, initialized: boolean, audience: ToastAudience): NotificationToast[] {
  const out: NotificationToast[] = [];
  for (const n of rows) {
    if (known.has(n.id)) continue;
    known.add(n.id);
    if (!initialized || n.read_at) continue;
    const t = toNotificationToast(n, audience);
    if (t) out.push(t);
  }
  return out;
}
